import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Tabs, Badge, Button } from '@teals/ui-web';
import {
  useGetAgentQuery,
  useUpdateAgentMutation,
  useGetSessionsQuery,
  useGetUploadUrlMutation,
  useGetDashboardQuery,
  useGetTaskAnalyticsQuery,
  type ChatMessage,
  type AttachmentRef,
  type SessionMessage,
} from '../../store/api';
import { useVoiceChat, usePlaygroundChat } from '../../hooks';
import { useWebSocketContext } from '../../providers/WebSocketProvider';
import { extractErrorMessage } from '../../utils/api';
import { SlideViewer, type Slide } from './SlideViewer';
import ThinkingBlock from '../ChatPlayground/ThinkingBlock';
import { OverviewTab } from './OverviewTab';
import { ConfigureTab } from './ConfigureTab';
import { SessionsTab } from './SessionsTab';
import { ResponseExplorer } from './ResponseExplorer';

/* ---------- styled components ---------- */
const PageBody = styled.div`
  height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;


const AgentHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 1.5rem;
  border-bottom: 1px solid var(--border);
  background: var(--bg-surface);
  flex-wrap: wrap;
  gap: 0.75rem;
  flex-shrink: 0;
`;

const AgentHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const BackArrow = styled.a`
  color: var(--text-muted);
  font-size: 1.25rem;
  cursor: pointer;
  transition: color 0.2s;
  text-decoration: none;
  display: flex;
  align-items: center;

  &:hover {
    color: var(--accent);
  }
`;

const AgentIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: var(--accent-subtle);
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.125rem;
`;

const AgentTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const AgentMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 0.125rem;
  flex-wrap: wrap;
`;

const VersionTag = styled.span`
  font-size: 0.75rem;
  color: var(--text-muted);
  font-family: 'SF Mono', 'Fira Code', monospace;
  background: var(--bg-elevated);
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
`;

const StatusBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;


const SectionTitle = styled.h3`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;




const ChatBubble = styled.div<{ $role: 'user' | 'ai' }>`
  max-width: 75%;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  font-size: 0.875rem;
  line-height: 1.6;
  align-self: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
  background: ${({ $role }) => ($role === 'user' ? 'var(--accent)' : 'var(--bg-elevated)')};
  color: ${({ $role }) => ($role === 'user' ? 'white' : 'var(--text-secondary)')};
  border-top-right-radius: ${({ $role }) => ($role === 'user' ? '4px' : '12px')};
  border-top-left-radius: ${({ $role }) => ($role === 'user' ? '12px' : '4px')};
`;


/* ---- File upload components ---- */

const FileStageArea = styled.div`
  padding: 0.5rem 1.25rem;
  border-top: 1px solid var(--border);
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
`;

const FileChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.5rem 0.25rem 0.625rem;
  background: var(--accent-subtle);
  border: 1px solid var(--accent);
  border-radius: 6px;
  font-size: 0.75rem;
  color: var(--accent);
  max-width: 200px;
`;

const FileChipName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileChipRemove = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  background: none;
  color: var(--accent);
  cursor: pointer;
  font-size: 0.875rem;
  line-height: 1;
  border-radius: 50%;
  flex-shrink: 0;

  &:hover {
    background: var(--accent);
    color: white;
  }
`;


const DropOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(99, 102, 241, 0.08);
  border: 2px dashed var(--accent);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  color: var(--accent);
  font-weight: 600;
  pointer-events: none;
  z-index: 10;
`;

/* ---- Smart Response Bubble components ---- */

const SuggestionChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-top: 0.75rem;
`;

const SuggestionChip = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 0.375rem 0.75rem;
  border-radius: 999px;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid var(--border);
  background: var(--bg-surface);
  color: var(--accent);
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: var(--accent);
    color: white;
    border-color: var(--accent);
  }
`;

const ResponseMeta = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.5rem;
  padding: 0;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 0.6875rem;
  color: var(--text-muted);
  cursor: pointer;

  &:hover { color: var(--text-secondary); }
`;

const MetaPanel = styled.div`
  margin-top: 0.375rem;
  padding: 0.5rem 0.75rem;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 0.75rem;
  color: var(--text-muted);
  font-family: 'SF Mono', 'Fira Code', monospace;
  white-space: pre-wrap;
  word-break: break-word;
`;

const BubbleFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const BubbleActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.6875rem;
  font-weight: 500;
  color: var(--accent);
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 6px;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s;

  &:hover {
    background: var(--accent-subtle);
    border-color: var(--accent);
  }

  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }
`;

const ExpandIconSvg = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="10 2 14 2 14 6" />
    <polyline points="6 14 2 14 2 10" />
    <line x1="14" y1="2" x2="9.5" y2="6.5" />
    <line x1="2" y1="14" x2="6.5" y2="9.5" />
  </svg>
);

const MarkdownWrap = styled.div`
  font-size: 0.875rem;
  line-height: 1.7;
  word-break: break-word;

  > *:first-child { margin-top: 0; }
  > *:last-child { margin-bottom: 0; }

  h1, h2, h3, h4 {
    margin: 0.75em 0 0.35em;
    line-height: 1.3;
    color: var(--text-primary);
  }
  h1 { font-size: 1.15em; }
  h2 { font-size: 1.05em; }
  h3 { font-size: 0.95em; }

  p { margin: 0.4em 0; }

  ul, ol {
    margin: 0.4em 0;
    padding-left: 1.4em;
  }
  li { margin: 0.15em 0; }
  li > p { margin: 0; }

  strong { color: var(--text-primary); }

  code {
    font-family: 'SF Mono', 'Fira Code', monospace;
    font-size: 0.8em;
    padding: 1px 5px;
    background: rgba(0, 0, 0, 0.15);
    border-radius: 3px;
  }

  pre {
    margin: 0.5em 0;
    padding: 0.75em 1em;
    background: rgba(0, 0, 0, 0.15);
    border-radius: 6px;
    overflow-x: auto;
    code {
      padding: 0;
      background: none;
      font-size: 0.8em;
    }
  }

  table {
    border-collapse: collapse;
    margin: 0.5em 0;
    font-size: 0.85em;
    width: 100%;
    th, td {
      padding: 0.35em 0.6em;
      border: 1px solid var(--border);
      text-align: left;
    }
    th { background: rgba(0, 0, 0, 0.08); font-weight: 600; }
  }

  blockquote {
    margin: 0.5em 0;
    padding: 0.25em 0.75em;
    border-left: 3px solid var(--accent);
    color: var(--text-muted);
  }

  a {
    color: var(--accent);
    text-decoration: underline;
    text-underline-offset: 2px;
    &:hover { text-decoration-thickness: 2px; }
  }

  hr {
    border: none;
    border-top: 1px solid var(--border);
    margin: 0.75em 0;
  }
`;

interface ParsedResponse {
  message: string;
  suggestions?: string[];
  meta?: Record<string, unknown>;
  slides?: Slide[];
  stage?: string;
}

function parseAgentResponse(content: string): ParsedResponse | null {
  const trimmed = content.trim();
  if (!trimmed.startsWith('{')) return null;
  try {
    const obj = JSON.parse(trimmed);
    if (typeof obj !== 'object' || obj === null) return null;
    const msg = obj.message || obj.response || obj.text || obj.answer;
    if (typeof msg !== 'string') return null;
    const suggestions = Array.isArray(obj.suggestions)
      ? obj.suggestions.filter((s: unknown) => typeof s === 'string')
      : undefined;
    const slides: Slide[] | undefined =
      Array.isArray(obj.slides) && obj.slides.length > 0
        ? obj.slides.filter(
            (s: unknown): s is Slide =>
              typeof s === 'object' && s !== null &&
              'slide_number' in s && 'title' in s && 'html' in s,
          )
        : undefined;
    const stage = typeof obj.stage === 'string' ? obj.stage : undefined;
    const { message: _m, response: _r, text: _t, answer: _a, suggestions: _s, slides: _sl, stage: _st, ...rest } = obj;
    const hasMeta = Object.keys(rest).length > 0;
    return { message: msg, suggestions, meta: hasMeta ? rest : undefined, slides, stage };
  } catch {
    return null;
  }
}

const MarkdownRenderer: React.FC<{ text: string }> = ({ text }) => {
  const hasMarkdown = /[#*_`\[\]|>~-]{2}|^\s*[-*+]\s|^\s*\d+\.\s|^#{1,6}\s/m.test(text);
  if (!hasMarkdown) return <>{text}</>;
  return (
    <MarkdownWrap>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
    </MarkdownWrap>
  );
};

const getExplorerContent = (content: string, parsed: ParsedResponse | null, hasSlides: boolean): string => {
  if (hasSlides) return content;
  if (parsed?.meta) return JSON.stringify(parsed.meta, null, 2);
  if (parsed) return parsed.message;
  return content;
};

const SmartBubble: React.FC<{
  content: string;
  onSuggestionClick?: (text: string) => void;
  onExploreClick?: (content: string) => void;
  onExpandClick?: (content: string) => void;
}> = ({ content, onSuggestionClick, onExploreClick, onExpandClick }) => {
  const [showMeta, setShowMeta] = React.useState(false);
  const parsed = parseAgentResponse(content);

  const hasSlides = !!(parsed?.slides && parsed.slides.length > 0);
  const expandContent = getExplorerContent(content, parsed ?? null, hasSlides);
  const showExpand = !!onExpandClick;
  const showExplore = !!onExploreClick;
  const showFooter = showExpand || showExplore;

  const mainContent = !parsed ? (
    <MarkdownRenderer text={content} />
  ) : (
    <>
      <MarkdownRenderer text={parsed.message} />
      {hasSlides && <SlideViewer slides={parsed.slides!} compact />}
      {parsed.suggestions && parsed.suggestions.length > 0 && (
        <SuggestionChips>
          {parsed.suggestions.map((s, i) => (
            <SuggestionChip key={i} onClick={() => onSuggestionClick?.(s)}>
              {s}
            </SuggestionChip>
          ))}
        </SuggestionChips>
      )}
      {parsed.meta && (
        <>
          <ResponseMeta onClick={() => setShowMeta((p) => !p)}>
            {showMeta ? '▾' : '▸'} details
          </ResponseMeta>
          {showMeta && (
            <MetaPanel>{JSON.stringify(parsed.meta, null, 2)}</MetaPanel>
          )}
        </>
      )}
    </>
  );

  return (
    <>
      {mainContent}
      {showFooter && (
        <BubbleFooter>
          {showExpand && (
            <BubbleActionBtn
              onClick={() => onExpandClick!(expandContent)}
              title="Expand to full view"
            >
              <ExpandIconSvg />
              <span>Expand</span>
            </BubbleActionBtn>
          )}
          {showExplore && (
            <BubbleActionBtn
              onClick={() => onExploreClick!(expandContent)}
              title="Open in Explorer panel"
            >
              &#x1F50D; Explore
            </BubbleActionBtn>
          )}
        </BubbleFooter>
      )}
    </>
  );
};

const LoadingContainer = styled.div`
  text-align: center;
  padding: 4rem 0;
  color: var(--text-muted);
  font-size: 0.9375rem;
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 4rem 0;
  color: var(--error);
  font-size: 0.9375rem;
`;

/* ---------- analytics tab styled components ---------- */
const AnalyticsLayout = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 2fr;
  overflow: hidden;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    overflow-y: auto;
  }
`;

const AnalyticsChatPanel = styled.div`
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--border);
  overflow: hidden;
`;

const AnalyticsChatWelcome = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  gap: 0.75rem;
`;

const AnalyticsChatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: var(--accent-subtle);
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
`;

const AnalyticsChatTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const AnalyticsChatDesc = styled.p`
  font-size: 0.8125rem;
  color: var(--text-muted);
  margin: 0;
  max-width: 280px;
  line-height: 1.5;
`;

const AnalyticsSuggestionChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
  margin-top: 0.5rem;
`;

const AnalyticsSuggestionChip = styled.button`
  padding: 0.375rem 0.75rem;
  border: 1px solid var(--border);
  border-radius: 999px;
  background: var(--bg-elevated);
  color: var(--text-secondary);
  font-size: 0.75rem;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
    background: var(--accent-subtle);
  }
`;

const AnalyticsChatInputArea = styled.div`
  display: flex;
  gap: 0.5rem;
  padding: 0.75rem;
  border-top: 1px solid var(--border);
`;

const AnalyticsChatInput = styled.input`
  flex: 1;
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 0.8125rem;
  font-family: inherit;
  outline: none;

  &:focus {
    border-color: var(--accent);
  }
`;

const PortletsPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow-y: auto;
  padding: 1.5rem;
`;

const PortletsPanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PortletDateRange = styled.div`
  display: flex;
  gap: 0.25rem;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 0.1875rem;
`;

const PortletDateBtn = styled.button<{ $active?: boolean }>`
  padding: 0.3125rem 0.625rem;
  border: none;
  border-radius: 6px;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background: ${({ $active }) => ($active ? 'var(--accent)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'white' : 'var(--text-secondary)')};

  &:hover {
    background: ${({ $active }) => ($active ? 'var(--accent)' : 'var(--bg-elevated)')};
  }
`;

const PortletGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Portlet = styled.div<{ $full?: boolean }>`
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
  grid-column: ${({ $full }) => ($full ? '1 / -1' : 'auto')};
`;

const PortletHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 1rem;
  border-bottom: 1px solid var(--border);
`;

const PortletTitle = styled.h4`
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const PortletToggle = styled.button`
  border: none;
  background: none;
  color: var(--text-muted);
  cursor: pointer;
  font-size: 0.75rem;
  padding: 0.25rem;
  border-radius: 4px;
  transition: color 0.2s;

  &:hover {
    color: var(--text-primary);
  }
`;

const PortletBody = styled.div`
  padding: 1rem;
`;

const KPIRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;

  @media (max-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const KPIItem = styled.div`
  text-align: center;
`;

const KPIItemLabel = styled.div`
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
  margin-bottom: 0.375rem;
`;

const KPIItemValue = styled.div`
  font-size: 1.375rem;
  font-weight: 700;
  color: var(--text-primary);
`;

const FailingTaskRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.625rem 0;
  border-bottom: 1px solid var(--border);

  &:last-child {
    border-bottom: none;
  }
`;

const FailingTaskName = styled.span`
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-primary);
`;

const FailingTaskCount = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--error);
  background: var(--error-subtle, rgba(239, 68, 68, 0.1));
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
`;

const EmptyPortletMsg = styled.div`
  text-align: center;
  padding: 1.5rem;
  font-size: 0.8125rem;
  color: var(--text-muted);
`;

const VoiceToggleBtn = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.625rem;
  border-radius: 6px;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid ${({ $active }) => ($active ? 'var(--accent)' : 'var(--border)')};
  background: ${({ $active }) => ($active ? 'var(--accent-subtle)' : 'var(--bg-surface)')};
  color: ${({ $active }) => ($active ? 'var(--accent)' : 'var(--text-secondary)')};
  transition: all 0.2s;
  white-space: nowrap;
  &:hover { border-color: var(--accent); color: var(--accent); }
`;

const MicBtn = styled.button<{ $recording?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 10px;
  background: ${({ $recording }) => ($recording ? 'var(--danger, #ef4444)' : 'var(--bg-surface)')};
  color: ${({ $recording }) => ($recording ? 'white' : 'var(--text-muted)')};
  border: 1px solid ${({ $recording }) => ($recording ? 'var(--danger, #ef4444)' : 'var(--border)')};
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.2s;
  flex-shrink: 0;
  &:hover {
    border-color: ${({ $recording }) => ($recording ? 'var(--danger, #dc2626)' : 'var(--accent)')};
    color: ${({ $recording }) => ($recording ? 'white' : 'var(--accent)')};
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const VoiceIndicator = styled.span<{ $color?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ $color }) => $color || 'var(--danger, #ef4444)'};
  animation: pulse 1.5s ease-in-out infinite;
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const VoiceError = styled.div`
  font-size: 0.6875rem;
  color: var(--danger, #ef4444);
  margin-top: 0.25rem;
`;

/* ---------- playground two-panel ---------- */
const PlaygroundContainer = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
  min-height: 0;
`;

const PlaygroundMain = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid var(--border);
  background: var(--bg-surface);
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
  flex-shrink: 0;
`;

const PanelHeaderActions = styled.span`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.375rem;
`;

const GhostBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.3125rem 0.5rem;
  border-radius: 6px;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid transparent;
  background: transparent;
  color: var(--text-muted);
  transition: all 0.2s;
  white-space: nowrap;
  &:hover { background: var(--bg-elevated); color: var(--text-primary); }
`;

const PlaygroundMessages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const PlaygroundWelcome = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  max-width: 560px;
  margin: 0 auto;
`;

const WelcomeIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: var(--accent-subtle);
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  margin: 0 auto 1.25rem;
`;

const WelcomeTitle = styled.h3`
  margin-bottom: 0.5rem;
  font-size: 1.125rem;
  color: var(--text-primary);
`;

const WelcomeDesc = styled.p`
  color: var(--text-muted);
  font-size: 0.9375rem;
  margin-bottom: 1.5rem;
  line-height: 1.5;
`;

const PgSuggestionChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
`;

const PgSuggestionChip = styled.button`
  padding: 0.5rem 1rem;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 999px;
  font-size: 0.8125rem;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  &:hover { border-color: var(--accent); color: var(--accent); background: var(--accent-subtle); }
`;

const PlaygroundInputArea = styled.div`
  padding: 1rem 1.5rem 1.5rem;
  border-top: 1px solid var(--border);
  background: var(--bg-surface);
  flex-shrink: 0;
`;

const PlaygroundInputOuter = styled.div`
  max-width: 720px;
  margin: 0 auto;
  background: var(--input-bg, var(--bg-primary));
  border: 1px solid var(--border);
  border-radius: 12px;
  transition: border-color 0.2s;
  position: relative;
  &:focus-within { border-color: var(--accent); }
`;

const PgInputRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  padding: 0.5rem;
`;

const PgTextarea = styled.textarea`
  flex: 1;
  padding: 0.375rem 0.5rem;
  border: none;
  background: transparent;
  font-family: inherit;
  font-size: 0.9375rem;
  color: var(--text-primary);
  outline: none;
  resize: none;
  max-height: 160px;
  line-height: 1.5;
  &::placeholder { color: var(--text-muted); }
`;

const PgActionBtn = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  transition: all 0.2s;
  flex-shrink: 0;
  &:hover { background: var(--bg-elevated); color: var(--text-primary); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const PgSendBtn = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: var(--accent);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  transition: all 0.2s;
  flex-shrink: 0;
  &:hover { background: var(--accent-hover); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const PlaygroundHint = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  text-align: center;
  margin-top: 0.5rem;
`;

const InspectModeTabs = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-left: 0.5rem;
`;

const InspectModeTab = styled.button<{ $active?: boolean }>`
  font-size: 0.6875rem;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  border: none;
  background: ${({ $active }) => ($active ? 'var(--accent-subtle)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--accent)' : 'var(--text-muted)')};
  cursor: pointer;
  font-family: inherit;

  &:hover {
    background: var(--bg-elevated);
    color: var(--text-secondary);
  }
`;

const InspectPanelInner = styled.aside`
  width: 320px;
  flex-shrink: 0;
  border-left: 1px solid var(--border);
  background: var(--bg-surface);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
`;

const TabletInspectOverlay = styled.div<{ $visible: boolean }>`
  display: ${({ $visible }) => ($visible ? 'flex' : 'none')};
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 320px;
  z-index: 200;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.3);
  background: var(--bg-surface);
  flex-direction: column;
  overflow: hidden;
`;

const InspectContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
`;

const ExplorerPlaceholder = styled.div`
  padding: 2rem 1rem;
  font-size: 0.8125rem;
  color: var(--text-muted);
  text-align: center;
  line-height: 1.6;
`;

const ExplorerFullscreenOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 300;
  background: var(--bg-overlay, rgba(15, 23, 42, 0.4));
  display: flex;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ExplorerFullscreenPanel = styled.div`
  position: absolute;
  top: 64px;
  left: 2rem;
  right: 2rem;
  bottom: 2rem;
  background: var(--bg-surface);
  border-radius: 16px;
  border: 1px solid var(--border);
  box-shadow: var(--shadow-xl);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: explorerScaleIn 0.25s ease;

  @keyframes explorerScaleIn {
    from { opacity: 0; transform: scale(0.96); }
    to { opacity: 1; transform: scale(1); }
  }

  @media (max-width: 768px) {
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 0;
  }
`;

const ExplorerFullscreenHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border);
  gap: 0.75rem;
  flex-shrink: 0;

  h4 {
    font-size: 0.9375rem;
    font-weight: 600;
    margin: 0;
  }
`;

const ExplorerFullscreenClose = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--bg-surface);
  color: var(--text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  margin-left: auto;
  transition: all 0.15s ease;

  &:hover {
    background: var(--error-subtle);
    border-color: var(--error);
    color: var(--error);
  }
`;

const ExplorerFullscreenBody = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const InspectSection = styled.div`
  margin-bottom: 1.25rem;
`;

const InspectSectionTitle = styled.div`
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
  margin-bottom: 0.5rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const InspectRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0;
  font-size: 0.8125rem;
  border-bottom: 1px solid var(--border);
  &:last-child { border-bottom: none; }
`;

const InspectKey = styled.span`
  color: var(--text-muted);
  flex-shrink: 0;
  white-space: nowrap;
`;

const InspectVal = styled.span`
  color: var(--text-primary);
  font-weight: 500;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const InspectCode = styled.div`
  background: var(--bg-elevated);
  border-radius: 8px;
  padding: 0.75rem;
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.75rem;
  color: var(--text-secondary);
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 180px;
  overflow: auto;
`;

/* ---------- helpers ---------- */

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'error' | 'neutral'> = {
  active: 'success',
  draft: 'warning',
  issues: 'error',
};

const TYPE_LABELS: Record<string, string> = {
  management: 'Management',
  conversational: 'Conversational',
  task: 'Task',
  general: 'General',
  data: 'Data',
};

/* ---------- file upload constants ---------- */
const ACCEPTED_TYPES = new Set([
  'image/jpeg', 'image/png', 'image/gif', 'image/webp',
  'application/pdf', 'text/csv', 'text/plain',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);

const FILE_ACCEPT = '.jpg,.jpeg,.png,.gif,.webp,.pdf,.csv,.txt,.docx';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

interface StagedFile {
  file: File;
  id: string;
}

/* ---------- component ---------- */
const VALID_TABS = ['overview', 'configure', 'sessions', 'analytics', 'playground'] as const;

const AgentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const initialTab = tabFromUrl && VALID_TABS.includes(tabFromUrl as any) ? tabFromUrl : 'overview';
  const [activeTab, setActiveTabState] = useState(initialTab);
  const setActiveTab = useCallback(
    (tab: string) => {
      setActiveTabState(tab);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (tab === 'overview') next.delete('tab');
        else next.set('tab', tab);
        return next;
      });
    },
    [setSearchParams],
  );
  useEffect(() => {
    const tab = searchParams.get('tab');
    const next = tab && VALID_TABS.includes(tab as (typeof VALID_TABS)[number]) ? tab : 'overview';
    setActiveTabState(next);
  }, [searchParams]);

  const { data: agent, isLoading, isError } = useGetAgentQuery(id!, { skip: !id });
  const [updateAgent, { isLoading: updating }] = useUpdateAgentMutation();
  const [sessionsPage, setSessionsPage] = useState(1);
  useEffect(() => {
    if (activeTab === 'sessions') setSessionsPage(1);
  }, [activeTab]);
  const { data: sessionsData, refetch: refetchSessions } = useGetSessionsQuery(
    { agentId: id, page: sessionsPage, limit: 20 },
    { skip: !id || activeTab !== 'sessions' },
  );
  useEffect(() => {
    if (activeTab === 'sessions' && id) refetchSessions();
  }, [activeTab, id, refetchSessions]);

  /* ---- analytics state ---- */
  type AnalyticsRange = '24h' | '7d' | '30d';
  const [analyticsRange, setAnalyticsRange] = useState<AnalyticsRange>('7d');
  const [collapsedPortlets, setCollapsedPortlets] = useState<Record<string, boolean>>({});

  const analyticsDateParams = useMemo(() => {
    const end = new Date();
    const start = new Date();
    if (analyticsRange === '24h') start.setDate(start.getDate() - 1);
    else if (analyticsRange === '7d') start.setDate(start.getDate() - 7);
    else start.setDate(start.getDate() - 30);
    return { startDate: start.toISOString(), endDate: end.toISOString() };
  }, [analyticsRange]);

  const { data: dashboardData, isLoading: dashLoading } = useGetDashboardQuery(
    analyticsDateParams,
    { skip: activeTab !== 'analytics' },
  );
  const { data: taskData, isLoading: tasksLoading } = useGetTaskAnalyticsQuery(
    analyticsDateParams,
    { skip: activeTab !== 'analytics' },
  );

  const togglePortlet = (key: string) =>
    setCollapsedPortlets((prev) => ({ ...prev, [key]: !prev[key] }));

  const formatAnalyticsNum = (n: number) =>
    n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `${(n / 1_000).toFixed(1)}K` : String(n);

  /* ---- playground state ---- */
  const { connectionState, connectionError: wsConnectionError } =
    useWebSocketContext();

  const [pgInput, setPgInput] = useState('');
  const [pgStagedFiles, setPgStagedFiles] = useState<StagedFile[]>([]);
  const [pgUploading, setPgUploading] = useState(false);
  const [pgDragOver, setPgDragOver] = useState(false);
  const [inspectOpen, setInspectOpen] = useState(true);
  const [inspectMode, setInspectMode] = useState<'inspector' | 'explorer'>('inspector');
  const [explorerContent, setExplorerContent] = useState<string | null>(null);
  const [explorerFullscreen, setExplorerFullscreen] = useState(false);
  const [isTablet, setIsTablet] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 1024 : false,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pgMessagesEndRef = useRef<HTMLDivElement>(null);

  const [getUploadUrl] = useGetUploadUrlMutation();

  useEffect(() => {
    const handler = () => setIsTablet(window.innerWidth <= 1024);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    if (!explorerFullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExplorerFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [explorerFullscreen]);

  const handleVoiceTranscript = useCallback((text: string) => {
    setPgInput(text);
  }, []);

  const {
    voiceEnabled,
    voiceState,
    error: voiceError,
    toggleVoice,
    startRecording,
    stopRecording,
    speak,
    stopSpeaking,
  } = useVoiceChat({ onTranscript: handleVoiceTranscript });

  const {
    messages: pgMessages,
    setMessages: setPgMessages,
    sessionId: pgSessionId,
    setSessionId: setPgSessionId,
    isProcessing: pgProcessing,
    isSending,
    thinkingBlocks: pgThinking,
    pipelineSteps: pgSteps,
    tasks: pgTasks,
    responsePreview: pgResponsePreview,
    warnings: pgWarnings,
    startTime: pgStartTime,
    chatError: pgError,
    setChatError: setPgError,
    sendMessage: pgSendMessage,
    newSession: pgNewSession,
  } = usePlaygroundChat({
    agentId: id!,
    agentName: agent?.agentName,
    forceNewOnFirstSend: true,
    onSpeak: voiceEnabled ? speak : undefined,
  });

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files) return;
    const newFiles: StagedFile[] = [];
    for (const file of Array.from(files)) {
      if (!ACCEPTED_TYPES.has(file.type)) continue;
      if (file.size > MAX_FILE_SIZE) continue;
      newFiles.push({ file, id: `${Date.now()}-${Math.random().toString(36).slice(2)}` });
    }
    setPgStagedFiles((prev) => [...prev, ...newFiles]);
  }, []);

  const removeStagedFile = useCallback((id: string) => {
    setPgStagedFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const uploadFile = useCallback(async (file: File): Promise<AttachmentRef> => {
    const resp = await getUploadUrl({
      filename: file.name,
      'content-type': file.type,
    }).unwrap();

    let uploadUrl = resp['upload-url'];
    const isSigV4 = uploadUrl.includes('X-Amz-Algorithm');
    if (!isSigV4 && uploadUrl.includes('.s3.amazonaws.com') && !uploadUrl.match(/\.s3\.[a-z]{2}[a-z0-9-]+\.amazonaws\.com/)) {
      uploadUrl = uploadUrl.replace('.s3.amazonaws.com', '.s3.ap-southeast-2.amazonaws.com');
    }

    const putResp = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
    });
    if (!putResp.ok) {
      throw new Error(`S3 upload failed: ${putResp.status}`);
    }

    return {
      's3-key': resp['s3-key'],
      'content-type': file.type,
      filename: file.name,
    };
  }, [getUploadUrl]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setPgDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setPgDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setPgDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  useEffect(() => {
    pgMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [pgMessages, pgThinking]);

  const handlePlaygroundSend = async () => {
    const text = pgInput.trim();
    const hasFiles = pgStagedFiles.length > 0;
    if ((!text && !hasFiles) || !id || !agent) return;

    if (hasFiles) {
      const fileNames = pgStagedFiles.map((f) => f.file.name);
      const displayContent = fileNames.length > 0
        ? `${text || ''}${text && fileNames.length ? '\n' : ''}${fileNames.map((n) => `\u{1F4CE} ${n}`).join(', ')}`
        : text;

      setPgUploading(true);
      try {
        const _attachments = await Promise.all(
          pgStagedFiles.map((sf) => uploadFile(sf.file))
        );
        setPgStagedFiles([]);
        setPgUploading(false);
        // TODO: pass attachments through shared hook when attachment support is added
        void _attachments;
      } catch (err) {
        setPgUploading(false);
        setPgError(extractErrorMessage(err));
        return;
      }
      void displayContent;
    }

    setPgInput('');
    pgSendMessage(text || 'Please process the attached file(s).');
  };

  const handlePlaygroundNewSession = () => {
    pgNewSession();
    setExplorerContent(null);
    setInspectMode('inspector');
    setPgStagedFiles([]);
    setPgUploading(false);
  };

  const handleOpenSessionInPlayground = useCallback(
    (sessionId: string, conversation: SessionMessage[]) => {
      const mapped: ChatMessage[] = conversation.map((m) => ({
        role: m.role as 'user' | 'assistant' | 'system',
        content: m.content,
      }));
      setPgSessionId(sessionId);
      setPgMessages(mapped);
      setActiveTab('playground');
    },
    [setPgSessionId, setPgMessages, setActiveTab],
  );

  const sessions = sessionsData?.sessions ?? [];
  const sessionsTotal = sessionsData?.total ?? 0;
  const sessionsLimit = sessionsData?.limit ?? 20;
  const sessionsTotalPages = Math.max(1, Math.ceil(sessionsTotal / sessionsLimit));

  const handlePublish = async () => {
    if (!id || !agent) return;
    await updateAgent({ id, body: { status: 'active' } });
  };

  const handleSaveToSandbox = async () => {
    if (!id || !agent) return;
    await updateAgent({ id, body: { status: 'draft' } });
  };

  if (isLoading) return <PageBody><LoadingContainer>Loading agent...</LoadingContainer></PageBody>;
  if (isError || !agent) return <PageBody><ErrorContainer>Failed to load agent.</ErrorContainer></PageBody>;

  const inspectPanelContent = (
    <>
      <PanelHeader>
        <span>&#x1F50D;</span>
        <InspectModeTabs>
          <InspectModeTab
            $active={inspectMode === 'inspector'}
            onClick={() => setInspectMode('inspector')}
          >
            Inspector
          </InspectModeTab>
          <InspectModeTab
            $active={inspectMode === 'explorer'}
            onClick={() => setInspectMode('explorer')}
          >
            Explorer
          </InspectModeTab>
        </InspectModeTabs>
        <PanelHeaderActions>
          <GhostBtn onClick={() => setInspectOpen(false)}>&#x2715;</GhostBtn>
        </PanelHeaderActions>
      </PanelHeader>
      {inspectMode === 'explorer' ? (
        explorerContent !== null ? (
          <ResponseExplorer
            content={explorerContent}
            onFullViewClick={() => setExplorerFullscreen(true)}
          />
        ) : (
          <InspectContent>
            <ExplorerPlaceholder>
              Select a message and click <strong>Explore</strong> to view its content here.
            </ExplorerPlaceholder>
          </InspectContent>
        )
      ) : (
      <InspectContent>
        <InspectSection>
          <InspectSectionTitle>Session Info</InspectSectionTitle>
          <InspectRow>
            <InspectKey>Session ID</InspectKey>
            <InspectVal style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
              {pgSessionId ? pgSessionId.slice(0, 12) : '\u2014'}
            </InspectVal>
          </InspectRow>
          <InspectRow>
            <InspectKey>Agent</InspectKey>
            <InspectVal>{agent.displayName ?? agent.agentName}</InspectVal>
          </InspectRow>
          <InspectRow>
            <InspectKey>Environment</InspectKey>
            <InspectVal><Badge variant="warning" size="sm">Sandbox</Badge></InspectVal>
          </InspectRow>
          <InspectRow>
            <InspectKey>Messages</InspectKey>
            <InspectVal>{pgMessages.length}</InspectVal>
          </InspectRow>
          <InspectRow>
            <InspectKey>Voice</InspectKey>
            <InspectVal>{voiceEnabled ? 'Enabled' : 'Off'}</InspectVal>
          </InspectRow>
        </InspectSection>

        <InspectSection>
          <InspectSectionTitle>Last LLM Call</InspectSectionTitle>
          <InspectRow>
            <InspectKey>Model</InspectKey>
            <InspectVal>{agent.config?.llmModel ?? 'N/A'}</InspectVal>
          </InspectRow>
          <InspectRow>
            <InspectKey>Tokens In</InspectKey>
            <InspectVal>&mdash;</InspectVal>
          </InspectRow>
          <InspectRow>
            <InspectKey>Tokens Out</InspectKey>
            <InspectVal>&mdash;</InspectVal>
          </InspectRow>
          <InspectRow>
            <InspectKey>Latency</InspectKey>
            <InspectVal>&mdash;</InspectVal>
          </InspectRow>
        </InspectSection>

        <InspectSection>
          <InspectSectionTitle>Agent Configuration</InspectSectionTitle>
          <InspectCode>
            {JSON.stringify({
              agent: agent.agentName,
              type: agent.agentType,
              model: agent.config?.llmModel ?? 'N/A',
              status: agent.status,
              version: 'sandbox',
            }, null, 2)}
          </InspectCode>
        </InspectSection>
      </InspectContent>
      )}
    </>
  );

  return (
    <PageBody className="fade-in">
        <AgentHeader>
          <AgentHeaderLeft>
            <BackArrow onClick={() => navigate('/agents')}>&larr;</BackArrow>
            <AgentIcon>{(agent.displayName ?? agent.agentName)?.[0] ?? '?'}</AgentIcon>
            <div>
              <AgentTitle>{agent.displayName ?? agent.agentName}</AgentTitle>
              <AgentMeta>
                <Badge variant={STATUS_VARIANT[agent.status] ?? 'neutral'} size="sm">
                  {agent.status}
                </Badge>
                <Badge variant="info" size="sm">
                  {TYPE_LABELS[agent.agentType] ?? agent.agentType}
                </Badge>
                <VersionTag>{agent.config?.llmModel ?? 'N/A'}</VersionTag>
              </AgentMeta>
            </div>
          </AgentHeaderLeft>
          <StatusBar>
            {agent.status !== 'active' && (
              <Button variant="primary" onClick={handlePublish} disabled={updating}>
                Publish
              </Button>
            )}
            <Button variant="secondary" onClick={handleSaveToSandbox} disabled={updating}>
              Save to Sandbox
            </Button>
          </StatusBar>
        </AgentHeader>

        <Tabs
          value={activeTab}
          onChange={setActiveTab}
          items={[
            { value: 'overview', label: 'Overview' },
            { value: 'configure', label: 'Configure' },
            { value: 'sessions', label: 'Sessions' },
            { value: 'analytics', label: 'Analytics' },
            { value: 'playground', label: 'Playground' },
          ]}
        />

        {activeTab === 'overview' && (
          <OverviewTab agent={agent} sessions={sessions} />
        )}

        {activeTab === 'configure' && (
          <ConfigureTab agent={agent} onSave={handleSaveToSandbox} isSaving={updating} />
        )}

        {activeTab === 'sessions' && (
          <SessionsTab
            agent={agent}
            sessions={sessions}
            page={sessionsPage}
            totalPages={sessionsTotalPages}
            onPageChange={setSessionsPage}
            onSwitchToPlayground={() => {
              handlePlaygroundNewSession();
              setActiveTab('playground');
            }}
            onOpenSessionInPlayground={handleOpenSessionInPlayground}
          />
        )}

        {activeTab === 'analytics' && (
          <AnalyticsLayout>
            <AnalyticsChatPanel>
              <AnalyticsChatWelcome>
                <AnalyticsChatIcon>&#x1F4CA;</AnalyticsChatIcon>
                <AnalyticsChatTitle>Analytics Assistant</AnalyticsChatTitle>
                <AnalyticsChatDesc>
                  Ask questions about this agent&apos;s performance, sessions, and error patterns in natural language.
                </AnalyticsChatDesc>
                <AnalyticsSuggestionChips>
                  <AnalyticsSuggestionChip>Why did the error rate spike?</AnalyticsSuggestionChip>
                  <AnalyticsSuggestionChip>Which sub-agent has the highest latency?</AnalyticsSuggestionChip>
                  <AnalyticsSuggestionChip>Show me session trends</AnalyticsSuggestionChip>
                </AnalyticsSuggestionChips>
              </AnalyticsChatWelcome>
              <AnalyticsChatInputArea>
                <AnalyticsChatInput placeholder="Ask about this agent's analytics..." />
                <Button variant="primary" size="sm" disabled>Send</Button>
              </AnalyticsChatInputArea>
            </AnalyticsChatPanel>

            <PortletsPanel>
              <PortletsPanelHeader>
                <SectionTitle style={{ margin: 0 }}>Dashboard</SectionTitle>
                <PortletDateRange>
                  {(['24h', '7d', '30d'] as const).map((r) => (
                    <PortletDateBtn key={r} $active={analyticsRange === r} onClick={() => setAnalyticsRange(r)}>
                      {r === '24h' ? 'Last 24h' : r === '7d' ? '7 days' : '30 days'}
                    </PortletDateBtn>
                  ))}
                </PortletDateRange>
              </PortletsPanelHeader>

              <Portlet $full>
                <PortletHeader>
                  <PortletTitle>Key Metrics</PortletTitle>
                  <PortletToggle onClick={() => togglePortlet('kpi')}>
                    {collapsedPortlets['kpi'] ? '▸' : '▾'}
                  </PortletToggle>
                </PortletHeader>
                {!collapsedPortlets['kpi'] && (
                  <PortletBody>
                    {dashLoading ? (
                      <KPIRow>
                        {Array.from({ length: 4 }).map((_, i) => (
                          <KPIItem key={i}>
                            <KPIItemLabel>&mdash;</KPIItemLabel>
                            <KPIItemValue>&mdash;</KPIItemValue>
                          </KPIItem>
                        ))}
                      </KPIRow>
                    ) : (
                      <KPIRow>
                        <KPIItem>
                          <KPIItemLabel>Total Sessions</KPIItemLabel>
                          <KPIItemValue>{formatAnalyticsNum(dashboardData?.totalSessions ?? 0)}</KPIItemValue>
                        </KPIItem>
                        <KPIItem>
                          <KPIItemLabel>Success Rate</KPIItemLabel>
                          <KPIItemValue>{(dashboardData?.successRate ?? 0).toFixed(1)}%</KPIItemValue>
                        </KPIItem>
                        <KPIItem>
                          <KPIItemLabel>Avg Response</KPIItemLabel>
                          <KPIItemValue>{((dashboardData?.avgResponseTime ?? 0) / 1000).toFixed(1)}s</KPIItemValue>
                        </KPIItem>
                        <KPIItem>
                          <KPIItemLabel>Tokens Used</KPIItemLabel>
                          <KPIItemValue>{formatAnalyticsNum(dashboardData?.tokensUsed ?? 0)}</KPIItemValue>
                        </KPIItem>
                      </KPIRow>
                    )}
                  </PortletBody>
                )}
              </Portlet>

              <PortletGrid>
                <Portlet>
                  <PortletHeader>
                    <PortletTitle>Session Volume</PortletTitle>
                    <PortletToggle onClick={() => togglePortlet('volume')}>
                      {collapsedPortlets['volume'] ? '▸' : '▾'}
                    </PortletToggle>
                  </PortletHeader>
                  {!collapsedPortlets['volume'] && (
                    <PortletBody>
                      <EmptyPortletMsg>
                        {sessions.length} total sessions recorded
                      </EmptyPortletMsg>
                    </PortletBody>
                  )}
                </Portlet>

                <Portlet>
                  <PortletHeader>
                    <PortletTitle>Fleet Usage</PortletTitle>
                    <PortletToggle onClick={() => togglePortlet('fleet')}>
                      {collapsedPortlets['fleet'] ? '▸' : '▾'}
                    </PortletToggle>
                  </PortletHeader>
                  {!collapsedPortlets['fleet'] && (
                    <PortletBody>
                      <EmptyPortletMsg>Fleet usage data will appear here</EmptyPortletMsg>
                    </PortletBody>
                  )}
                </Portlet>

                <Portlet>
                  <PortletHeader>
                    <PortletTitle>Delegation Flow</PortletTitle>
                    <PortletToggle onClick={() => togglePortlet('delegation')}>
                      {collapsedPortlets['delegation'] ? '▸' : '▾'}
                    </PortletToggle>
                  </PortletHeader>
                  {!collapsedPortlets['delegation'] && (
                    <PortletBody>
                      <EmptyPortletMsg>Delegation flow data will appear here</EmptyPortletMsg>
                    </PortletBody>
                  )}
                </Portlet>

                <Portlet>
                  <PortletHeader>
                    <PortletTitle>Top Failing Tasks</PortletTitle>
                    <PortletToggle onClick={() => togglePortlet('failing')}>
                      {collapsedPortlets['failing'] ? '▸' : '▾'}
                    </PortletToggle>
                  </PortletHeader>
                  {!collapsedPortlets['failing'] && (
                    <PortletBody>
                      {tasksLoading ? (
                        <EmptyPortletMsg>&mdash;</EmptyPortletMsg>
                      ) : (taskData?.topFailingTasks?.length ?? 0) > 0 ? (
                        taskData!.topFailingTasks.map((t) => (
                          <FailingTaskRow key={t.taskName}>
                            <FailingTaskName>{t.taskName}</FailingTaskName>
                            <FailingTaskCount>{t.failureCount} failures</FailingTaskCount>
                          </FailingTaskRow>
                        ))
                      ) : (
                        <EmptyPortletMsg>No failing tasks</EmptyPortletMsg>
                      )}
                    </PortletBody>
                  )}
                </Portlet>
              </PortletGrid>
            </PortletsPanel>
          </AnalyticsLayout>
        )}

        {activeTab === 'playground' && (
          <PlaygroundContainer>
            {isTablet ? (
            <>
            <PlaygroundMain>
              <PanelHeader>
                <span>&#x1F9EA;</span> Sandbox Chat
                {connectionState === 'connected' && (
                  <Badge variant="success" size="sm">&#x25CF; Live</Badge>
                )}
                {(connectionState === 'connecting' || connectionState === 'reconnecting') && (
                  <Badge variant="warning" size="sm">&#x25CF; {connectionState === 'connecting' ? 'Connecting...' : 'Reconnecting...'}</Badge>
                )}
                {connectionState === 'error' && (
                  <Badge variant="error" size="sm">&#x25CF; Offline</Badge>
                )}
                {pgSessionId && (
                  <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                    {pgSessionId.slice(0, 8)}
                  </span>
                )}
                <PanelHeaderActions>
                  <VoiceToggleBtn
                    $active={voiceEnabled}
                    onClick={toggleVoice}
                    title={voiceEnabled ? 'Disable voice mode' : 'Enable voice mode'}
                  >
                    {voiceEnabled ? '\u{1F50A}' : '\u{1F507}'} Voice {voiceEnabled ? 'On' : 'Off'}
                  </VoiceToggleBtn>
                  <GhostBtn onClick={() => setInspectOpen((p) => !p)}>
                    &#x1F50D; Inspect
                  </GhostBtn>
                  <GhostBtn onClick={handlePlaygroundNewSession}>
                    &#x2795; New
                  </GhostBtn>
                </PanelHeaderActions>
              </PanelHeader>

              {pgError && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  padding: '0.75rem 1rem',
                  background: 'rgba(239, 68, 68, 0.08)',
                  borderBottom: '1px solid rgba(239, 68, 68, 0.2)',
                  color: '#dc2626', fontSize: '0.875rem', lineHeight: '1.4',
                }}>
                  <span style={{ flex: 1 }}>{pgError}</span>
                  <button
                    onClick={() => setPgError(null)}
                    style={{
                      background: 'none', border: 'none', color: '#dc2626',
                      cursor: 'pointer', fontSize: '1.125rem', padding: '0 0.25rem',
                      opacity: 0.6,
                    }}
                  >&times;</button>
                </div>
              )}

              {wsConnectionError && (
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
                  padding: '0.75rem 1rem',
                  background: 'rgba(234, 179, 8, 0.12)',
                  borderBottom: '1px solid rgba(234, 179, 8, 0.35)',
                  color: '#a16207', fontSize: '0.875rem', lineHeight: '1.45',
                }}>
                  <span style={{ flex: 1 }}>{wsConnectionError} Chat still works via polling when you send a message.</span>
                </div>
              )}

              <PlaygroundMessages
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={{ position: 'relative' }}
              >
                {pgDragOver && <DropOverlay>Drop files here</DropOverlay>}
                {pgMessages.length === 0 && (
                  <PlaygroundWelcome>
                    <WelcomeIcon>&#x1F9EA;</WelcomeIcon>
                    <WelcomeTitle>Test {agent.displayName ?? agent.agentName}</WelcomeTitle>
                    <WelcomeDesc>
                      Start a new session to interact with {agent.displayName ?? agent.agentName} in the sandbox environment.
                      Sessions created here will appear in the Sessions tab.
                    </WelcomeDesc>
                    <PgSuggestionChips>
                      {agent.agentType === 'conversational' ? (
                        <>
                          <PgSuggestionChip onClick={() => setPgInput('Hello, I\'m ready to start')}>
                            Hello, I&apos;m ready to start
                          </PgSuggestionChip>
                          <PgSuggestionChip onClick={() => setPgInput('What will you be evaluating today?')}>
                            What will you be evaluating today?
                          </PgSuggestionChip>
                        </>
                      ) : (
                        <>
                          <PgSuggestionChip onClick={() => setPgInput('Hello!')}>Hello!</PgSuggestionChip>
                          <PgSuggestionChip onClick={() => setPgInput('What can you help with?')}>
                            What can you help with?
                          </PgSuggestionChip>
                        </>
                      )}
                    </PgSuggestionChips>
                  </PlaygroundWelcome>
                )}
                {pgMessages.map((msg, i) => {
                  const isLastUser = msg.role === 'user' &&
                    pgMessages.slice(i + 1).every((m) => m.role !== 'user');
                  const showThinking = isLastUser &&
                    (pgProcessing || pgSteps.length > 0 || pgThinking.length > 0);
                  return (
                    <React.Fragment key={i}>
                      <ChatBubble $role={msg.role === 'user' ? 'user' : 'ai'}>
                        {msg.role === 'user' ? (
                          msg.content
                        ) : (
                          <SmartBubble
                            content={msg.content}
                            onSuggestionClick={(text) => setPgInput(text)}
                            onExploreClick={(content) => {
                              setExplorerContent(content);
                              setInspectMode('explorer');
                              setInspectOpen(true);
                            }}
                            onExpandClick={(content) => {
                              setExplorerContent(content);
                              setExplorerFullscreen(true);
                            }}
                          />
                        )}
                      </ChatBubble>
                      {showThinking && (
                        <ThinkingBlock
                          blocks={pgThinking}
                          steps={pgSteps}
                          tasks={pgTasks}
                          responsePreview={pgResponsePreview}
                          warnings={pgWarnings}
                          isActive={pgProcessing}
                          startTime={pgStartTime}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
                <div ref={pgMessagesEndRef} />
              </PlaygroundMessages>

              {pgStagedFiles.length > 0 && (
                <FileStageArea>
                  {pgStagedFiles.map((sf) => (
                    <FileChip key={sf.id}>
                      <FileChipName>{sf.file.name}</FileChipName>
                      <FileChipRemove onClick={() => removeStagedFile(sf.id)}>×</FileChipRemove>
                    </FileChip>
                  ))}
                </FileStageArea>
              )}

              <PlaygroundInputArea>
                {voiceState === 'recording' && (
                  <VoiceIndicator style={{ marginBottom: '0.375rem' }}>
                    &#x1F534; Recording&hellip; tap mic to stop
                  </VoiceIndicator>
                )}
                {voiceState === 'transcribing' && (
                  <VoiceIndicator $color="var(--accent)" style={{ marginBottom: '0.375rem' }}>
                    Transcribing&hellip;
                  </VoiceIndicator>
                )}
                {voiceState === 'speaking' && (
                  <VoiceIndicator
                    $color="var(--success, #22c55e)"
                    style={{ marginBottom: '0.375rem', cursor: 'pointer' }}
                    onClick={stopSpeaking}
                  >
                    &#x1F50A; Speaking&hellip; click to stop
                  </VoiceIndicator>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={FILE_ACCEPT}
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => { handleFileSelect(e.target.files); e.target.value = ''; }}
                />
                <PlaygroundInputOuter>
                  <PgInputRow>
                    <PgActionBtn
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isSending || pgUploading}
                      title="Attach file"
                    >
                      &#x1F4CE;
                    </PgActionBtn>
                    {voiceEnabled && (
                      <MicBtn
                        $recording={voiceState === 'recording'}
                        title={voiceState === 'recording' ? 'Stop recording' : 'Start recording'}
                        onClick={voiceState === 'recording' ? stopRecording : startRecording}
                        disabled={voiceState === 'transcribing' || voiceState === 'speaking' || pgProcessing}
                      >
                        {voiceState === 'recording' ? '\u23F9' : '\u{1F3A4}'}
                      </MicBtn>
                    )}
                    <PgTextarea
                      rows={1}
                      placeholder={voiceEnabled ? 'Type or tap the mic...' : `Send a message to ${agent.displayName ?? agent.agentName}...`}
                      value={pgInput}
                      onChange={(e) => setPgInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (pgInput.trim() || pgStagedFiles.length > 0) && !e.shiftKey) {
                          e.preventDefault();
                          handlePlaygroundSend();
                        }
                      }}
                      disabled={isSending || pgUploading || voiceState === 'recording'}
                    />
                    <PgSendBtn
                      title="Send"
                      onClick={handlePlaygroundSend}
                      disabled={isSending || pgUploading || (!pgInput.trim() && pgStagedFiles.length === 0) || voiceState === 'recording'}
                    >
                      &#x2191;
                    </PgSendBtn>
                  </PgInputRow>
                </PlaygroundInputOuter>
                {voiceError && <VoiceError>{voiceError}</VoiceError>}
                <PlaygroundHint>
                  Press Enter to send &middot; Shift+Enter for new line &middot; &#x1F4CE; Attach files
                  {voiceEnabled && <> &middot; &#x1F3A4; Mic to speak</>}
                  {' '}&middot; Running in <strong>Sandbox</strong> environment
                </PlaygroundHint>
              </PlaygroundInputArea>
            </PlaygroundMain>
            <TabletInspectOverlay $visible={inspectOpen}>
              {inspectPanelContent}
            </TabletInspectOverlay>
            </>
            ) : (
            <>
              <PlaygroundMain>
                <PanelHeader>
                  <span>&#x1F9EA;</span> Sandbox Chat
                  {connectionState === 'connected' && (
                    <Badge variant="success" size="sm">&#x25CF; Live</Badge>
                  )}
                  {(connectionState === 'connecting' || connectionState === 'reconnecting') && (
                    <Badge variant="warning" size="sm">&#x25CF; {connectionState === 'connecting' ? 'Connecting...' : 'Reconnecting...'}</Badge>
                  )}
                  {connectionState === 'error' && (
                    <Badge variant="error" size="sm">&#x25CF; Offline</Badge>
                  )}
                  {pgSessionId && (
                    <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                      {pgSessionId.slice(0, 8)}
                    </span>
                  )}
                  <PanelHeaderActions>
                    <VoiceToggleBtn
                      $active={voiceEnabled}
                      onClick={toggleVoice}
                      title={voiceEnabled ? 'Disable voice mode' : 'Enable voice mode'}
                    >
                      {voiceEnabled ? '\u{1F50A}' : '\u{1F507}'} Voice {voiceEnabled ? 'On' : 'Off'}
                    </VoiceToggleBtn>
                    <GhostBtn onClick={() => setInspectOpen((p) => !p)}>
                      &#x1F50D; Inspect
                    </GhostBtn>
                    <GhostBtn onClick={handlePlaygroundNewSession}>
                      &#x2795; New
                    </GhostBtn>
                  </PanelHeaderActions>
                </PanelHeader>

                {pgError && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                    padding: '0.75rem 1rem',
                    background: 'rgba(239, 68, 68, 0.08)',
                    borderBottom: '1px solid rgba(239, 68, 68, 0.2)',
                    color: '#dc2626', fontSize: '0.875rem', lineHeight: '1.4',
                  }}>
                    <span style={{ flex: 1 }}>{pgError}</span>
                    <button
                      onClick={() => setPgError(null)}
                      style={{
                        background: 'none', border: 'none', color: '#dc2626',
                        cursor: 'pointer', fontSize: '1.125rem', padding: '0 0.25rem',
                        opacity: 0.6,
                      }}
                    >&times;</button>
                  </div>
                )}

                {wsConnectionError && (
                  <div style={{
                    display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
                    padding: '0.75rem 1rem',
                    background: 'rgba(234, 179, 8, 0.12)',
                    borderBottom: '1px solid rgba(234, 179, 8, 0.35)',
                    color: '#a16207', fontSize: '0.875rem', lineHeight: '1.45',
                  }}>
                    <span style={{ flex: 1 }}>{wsConnectionError} Chat still works via polling when you send a message.</span>
                  </div>
                )}

                <PlaygroundMessages
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  style={{ position: 'relative' }}
                >
                  {pgDragOver && <DropOverlay>Drop files here</DropOverlay>}
                  {pgMessages.length === 0 && (
                    <PlaygroundWelcome>
                      <WelcomeIcon>&#x1F9EA;</WelcomeIcon>
                      <WelcomeTitle>Test {agent.displayName ?? agent.agentName}</WelcomeTitle>
                      <WelcomeDesc>
                        Start a new session to interact with {agent.displayName ?? agent.agentName} in the sandbox environment.
                        Sessions created here will appear in the Sessions tab.
                      </WelcomeDesc>
                      <PgSuggestionChips>
                        {agent.agentType === 'conversational' ? (
                          <>
                            <PgSuggestionChip onClick={() => setPgInput('Hello, I\'m ready to start')}>
                              Hello, I&apos;m ready to start
                            </PgSuggestionChip>
                            <PgSuggestionChip onClick={() => setPgInput('What will you be evaluating today?')}>
                              What will you be evaluating today?
                            </PgSuggestionChip>
                          </>
                        ) : (
                          <>
                            <PgSuggestionChip onClick={() => setPgInput('Hello!')}>Hello!</PgSuggestionChip>
                            <PgSuggestionChip onClick={() => setPgInput('What can you help with?')}>
                              What can you help with?
                            </PgSuggestionChip>
                          </>
                        )}
                      </PgSuggestionChips>
                    </PlaygroundWelcome>
                  )}
                  {pgMessages.map((msg, i) => {
                    const isLastUser = msg.role === 'user' &&
                      pgMessages.slice(i + 1).every((m) => m.role !== 'user');
                    const showThinking = isLastUser &&
                      (pgProcessing || pgSteps.length > 0 || pgThinking.length > 0);
                    return (
                      <React.Fragment key={i}>
                        <ChatBubble $role={msg.role === 'user' ? 'user' : 'ai'}>
                          {msg.role === 'user' ? (
                            msg.content
                          ) : (
                            <SmartBubble
                              content={msg.content}
                              onSuggestionClick={(text) => setPgInput(text)}
                              onExploreClick={(content) => {
                                setExplorerContent(content);
                                setInspectMode('explorer');
                                setInspectOpen(true);
                              }}
                              onExpandClick={(content) => {
                                setExplorerContent(content);
                                setExplorerFullscreen(true);
                              }}
                            />
                          )}
                        </ChatBubble>
                        {showThinking && (
                          <ThinkingBlock
                            blocks={pgThinking}
                            steps={pgSteps}
                            tasks={pgTasks}
                            responsePreview={pgResponsePreview}
                            warnings={pgWarnings}
                            isActive={pgProcessing}
                            startTime={pgStartTime}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                  <div ref={pgMessagesEndRef} />
                </PlaygroundMessages>

                {pgStagedFiles.length > 0 && (
                  <FileStageArea>
                    {pgStagedFiles.map((sf) => (
                      <FileChip key={sf.id}>
                        <FileChipName>{sf.file.name}</FileChipName>
                        <FileChipRemove onClick={() => removeStagedFile(sf.id)}>×</FileChipRemove>
                      </FileChip>
                    ))}
                  </FileStageArea>
                )}

                <PlaygroundInputArea>
                  {voiceState === 'recording' && (
                    <VoiceIndicator style={{ marginBottom: '0.375rem' }}>
                      &#x1F534; Recording&hellip; tap mic to stop
                    </VoiceIndicator>
                  )}
                  {voiceState === 'transcribing' && (
                    <VoiceIndicator $color="var(--accent)" style={{ marginBottom: '0.375rem' }}>
                      Transcribing&hellip;
                    </VoiceIndicator>
                  )}
                  {voiceState === 'speaking' && (
                    <VoiceIndicator
                      $color="var(--success, #22c55e)"
                      style={{ marginBottom: '0.375rem', cursor: 'pointer' }}
                      onClick={stopSpeaking}
                    >
                      &#x1F50A; Speaking&hellip; click to stop
                    </VoiceIndicator>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={FILE_ACCEPT}
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) => { handleFileSelect(e.target.files); e.target.value = ''; }}
                  />
                  <PlaygroundInputOuter>
                    <PgInputRow>
                      <PgActionBtn
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isSending || pgUploading}
                        title="Attach file"
                      >
                        &#x1F4CE;
                      </PgActionBtn>
                      {voiceEnabled && (
                        <MicBtn
                          $recording={voiceState === 'recording'}
                          title={voiceState === 'recording' ? 'Stop recording' : 'Start recording'}
                          onClick={voiceState === 'recording' ? stopRecording : startRecording}
                          disabled={voiceState === 'transcribing' || voiceState === 'speaking' || pgProcessing}
                        >
                          {voiceState === 'recording' ? '\u23F9' : '\u{1F3A4}'}
                        </MicBtn>
                      )}
                      <PgTextarea
                        rows={1}
                        placeholder={voiceEnabled ? 'Type or tap the mic...' : `Send a message to ${agent.displayName ?? agent.agentName}...`}
                        value={pgInput}
                        onChange={(e) => setPgInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && (pgInput.trim() || pgStagedFiles.length > 0) && !e.shiftKey) {
                            e.preventDefault();
                            handlePlaygroundSend();
                          }
                        }}
                        disabled={isSending || pgUploading || voiceState === 'recording'}
                      />
                      <PgSendBtn
                        title="Send"
                        onClick={handlePlaygroundSend}
                        disabled={isSending || pgUploading || (!pgInput.trim() && pgStagedFiles.length === 0) || voiceState === 'recording'}
                      >
                        &#x2191;
                      </PgSendBtn>
                    </PgInputRow>
                  </PlaygroundInputOuter>
                  {voiceError && <VoiceError>{voiceError}</VoiceError>}
                  <PlaygroundHint>
                    Press Enter to send &middot; Shift+Enter for new line &middot; &#x1F4CE; Attach files
                    {voiceEnabled && <> &middot; &#x1F3A4; Mic to speak</>}
                    {' '}&middot; Running in <strong>Sandbox</strong> environment
                  </PlaygroundHint>
                </PlaygroundInputArea>
              </PlaygroundMain>
              {inspectOpen && (
                <InspectPanelInner>
                  {inspectPanelContent}
                </InspectPanelInner>
              )}
            </>
            )}
          </PlaygroundContainer>
        )}

      {explorerFullscreen && explorerContent !== null && (
        <ExplorerFullscreenOverlay onClick={(e) => {
          if (e.target === e.currentTarget) setExplorerFullscreen(false);
        }}>
          <ExplorerFullscreenPanel>
            <ExplorerFullscreenHeader>
              <span>&#x1F50D;</span>
              <h4>Explorer</h4>
              <ExplorerFullscreenClose
                onClick={() => setExplorerFullscreen(false)}
                title="Close"
              >
                &#x2715;
              </ExplorerFullscreenClose>
            </ExplorerFullscreenHeader>
            <ExplorerFullscreenBody>
              <ResponseExplorer content={explorerContent} />
            </ExplorerFullscreenBody>
          </ExplorerFullscreenPanel>
        </ExplorerFullscreenOverlay>
      )}
    </PageBody>
  );
};

export default AgentDetail;
